import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, splitLanguagePath } from '../i18n/locales';

const SITE_NAME = 'BusyBee.Day';
const SITE_URL = 'https://busybee.day';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

const PUBLISHER_ORG = {
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/icon-512.png`,
    width: 512,
    height: 512,
  },
};

const buildFaqSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  })),
});

const buildBreadcrumbSchema = (crumbs) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: crumbs.map((crumb, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: crumb.name,
    item: crumb.url ? `${SITE_URL}${crumb.url}` : undefined,
  })),
});

const buildHowToSchema = (howTo) => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: howTo.name,
  description: howTo.description,
  ...(howTo.totalTime ? { totalTime: howTo.totalTime } : {}),
  step: howTo.steps.map((step, i) => ({
    '@type': 'HowToStep',
    position: i + 1,
    name: step.name,
    text: step.text,
    ...(step.image ? { image: `${SITE_URL}${step.image}` } : {}),
    ...(step.url ? { url: `${SITE_URL}${step.url}` } : {}),
  })),
});

const buildSoftwareAppSchema = (sa, t) => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: sa.name || SITE_NAME,
  applicationCategory: sa.applicationCategory || 'BusinessApplication',
  applicationSubCategory: sa.applicationSubCategory,
  // The desktop and phone apps are not public yet (/download): the browser is what people can use.
  operatingSystem: sa.operatingSystem || 'Web',
  url: sa.url || SITE_URL,
  description: sa.description || t('ui_common.seo.default_description'),
  ...(sa.featureList ? { featureList: sa.featureList } : {}),
  ...(sa.screenshot ? { screenshot: typeof sa.screenshot === 'string' ? `${SITE_URL}${sa.screenshot}` : sa.screenshot } : {}),
  offers: sa.offers || {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    description: t('ui_common.seo.free_plan'),
  },
  // No aggregateRating: there is no public review source to back one, and made-up
  // ratings are misleading markup (Google structured data policies).
  publisher: PUBLISHER_ORG,
});

// Who runs the site and the site itself, for the home page.
const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  ...PUBLISHER_ORG,
  legalName: 'NeoPixel s.r.o.',
  sameAs: [],
};
const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  publisher: PUBLISHER_ORG,
};

// i18next can report the browser locale (en-US); URLs and hreflang use the bare code.
const baseLanguage = (lng) => {
  const code = (lng || DEFAULT_LANGUAGE).split('-')[0].toLowerCase();
  return SUPPORTED_LANGUAGES.some((l) => l.code === code) ? code : DEFAULT_LANGUAGE;
};

// Search results show about 60 characters of a title. The site name goes after the
// page's own words only when it fits and is not already there; the home page leads
// with it, so a search for the brand finds the home page.
const TITLE_MAX = 60;
export const composeTitle = (title, { brandFirst = false } = {}) => {
  if (title.includes(SITE_NAME)) return title;
  if (brandFirst) return `${SITE_NAME}: ${title}`;
  const withName = `${title} | ${SITE_NAME}`;
  return withName.length <= TITLE_MAX ? withName : title;
};

const buildArticleSchema = (article) => ({
  '@context': 'https://schema.org',
  '@type': article.type || 'Article',
  headline: article.headline,
  description: article.description,
  image: article.image ? `${SITE_URL}${article.image}` : DEFAULT_IMAGE,
  datePublished: article.datePublished,
  dateModified: article.dateModified || article.datePublished,
  author: article.author && {
    '@type': 'Organization',
    name: article.author.name,
    url: article.author.url || SITE_URL,
  },
  publisher: PUBLISHER_ORG,
});

const SEO = ({
  title,
  description: descriptionProp,
  canonical,
  type = 'website',
  image = DEFAULT_IMAGE,
  noindex = false,
  jsonLd,
  faqItems,
  breadcrumbs,
  howTo,
  softwareApp,
  article,
  keywords,
  englishOnly = false,
}) => {
  const { t, i18n } = useTranslation();
  const lang = baseLanguage(i18n.resolvedLanguage || i18n.language);
  const description = descriptionProp ?? t('ui_common.seo.default_description');

  // Strip any /<lang>/ prefix from the canonical so we can derive per-language alternates.
  const canonicalNoLang = canonical ? splitLanguagePath(canonical).rest : null;
  const fullTitle = title
    ? composeTitle(title, { brandFirst: canonicalNoLang === '/' })
    : `${SITE_NAME} - ${t('ui_common.seo.default_tagline')}`;
  // A page written only in English (the legal pages) has one address: no language
  // copies, no hreflang alternates; scripts/prerender.mjs reads busybee:languages.
  const prefix = lang !== DEFAULT_LANGUAGE && !englishOnly ? `/${lang}` : '';
  const canonicalUrl = canonicalNoLang
    ? `${SITE_URL}${prefix}${canonicalNoLang === '/' ? '' : canonicalNoLang}`
    : undefined;
  const fullImage = image && image.startsWith('http') ? image : `${SITE_URL}${image || ''}`;

  const alternates = canonicalNoLang && !englishOnly
    ? SUPPORTED_LANGUAGES.map((lang) => ({
        code: lang.code,
        href: `${SITE_URL}${lang.code === DEFAULT_LANGUAGE ? '' : `/${lang.code}`}${canonicalNoLang === '/' ? '' : canonicalNoLang}`,
      }))
    : [];

  const faqSchema = faqItems?.length ? buildFaqSchema(faqItems) : null;
  const breadcrumbSchema = breadcrumbs?.length ? buildBreadcrumbSchema(breadcrumbs) : null;
  const howToSchema = howTo ? buildHowToSchema(howTo) : null;
  const softwareAppSchema = softwareApp ? buildSoftwareAppSchema(softwareApp, t) : null;
  const articleSchema = article ? buildArticleSchema(article) : null;
  const isHome = canonicalNoLang === '/';
  const homeSchemas = isHome ? [ORGANIZATION_SCHEMA, WEBSITE_SCHEMA,
    buildSoftwareAppSchema({ applicationCategory: 'BusinessApplication', applicationSubCategory: t('ui_common.seo.app_subcategory') }, t)] : [];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && <meta name="robots" content="max-image-preview:large" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {englishOnly && <meta name="busybee:languages" content="en" />}
      {alternates.map((alt) => (
        <link key={alt.code} rel="alternate" hrefLang={alt.code} href={alt.href} />
      ))}
      {alternates.length > 0 && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={alternates.find((a) => a.code === DEFAULT_LANGUAGE)?.href}
        />
      )}
      <html lang={lang} />
      <meta property="og:locale" content={lang} />

      {/* Open Graph */}
      <meta property="og:type" content={article ? 'article' : type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={SITE_NAME} />
      {fullImage && <meta property="og:image" content={fullImage} />}
      {fullImage && <meta property="og:image:width" content="1200" />}
      {fullImage && <meta property="og:image:height" content="630" />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {fullImage && <meta name="twitter:image" content={fullImage} />}

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}
      {howToSchema && (
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
      )}
      {softwareAppSchema && (
        <script type="application/ld+json">{JSON.stringify(softwareAppSchema)}</script>
      )}
      {articleSchema && (
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      )}
      {homeSchemas.map((schema) => (
        <script key={schema['@type']} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}
    </Helmet>
  );
};

export default SEO;
